'use client';

import Link from 'next/link';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from 'src/auth/hooks';
import { paths } from 'src/routes/paths';
import { Card, Button } from 'src/components/ui/primitives';
import { WorkspaceShortcuts } from 'src/components/workspace-shortcuts';
import {
  useGetDashboardCountDataMobile,
  useGetDashboardChartCount,
  useGetDashboradProjectCount,
  useGetClientDashboard,
} from 'src/actions/dashboard/dashboardActions';
import styles from './overview-app-view.module.css';

const statuses = [
  ['inProgress', 'inProgressCount', '#EA571A'],
  ['onHold', 'onHoldCount', '#8eafb8'],
  ['completed', 'completedCount', '#4f8b80'],
  ['notStarted', 'notStartedCount', '#074787'],
  ['submitted', 'requestedCount', '#608ba4'],
];
function Panel({ title, href, action, children, className = '' }) {
  return (
    <Card className={`${styles.panel} ${className}`}>
      <div className={styles.panelHeading}>
        <h2>{title}</h2>
        {href && (
          <Button variant="ghost" asChild>
            <Link href={href}>{action}</Link>
          </Button>
        )}
      </div>
      {children}
    </Card>
  );
}
function Bars({ data = {}, t }) {
  const max = Math.max(1, ...statuses.map(([, key]) => Number(data[key]) || 0));
  return (
    <div
      className={styles.bars}
      role="img"
      aria-label={statuses.map(([label, key]) => `${t(label)}: ${data[key] || 0}`).join(', ')}
    >
      {statuses.map(([label, key, color]) => (
        <div key={key} className={styles.barColumn}>
          <span className={styles.barValue}>{data[key] || 0}</span>
          <div className={styles.barTrack}>
            <div
              style={{
                height: `${Math.max(2, ((data[key] || 0) / max) * 100)}%`,
                background: color,
              }}
            />
          </div>
          <span>{t(label)}</span>
        </div>
      ))}
    </div>
  );
}
export function OverviewAppView() {
  const { t, i18n } = useTranslation('dashboard/dashboard-overview');
  const { employeeId, tairraUser } = useAuthContext();
  const counts = useGetDashboardCountDataMobile(employeeId);
  const charts = useGetDashboardChartCount(employeeId);
  const projectResult = useGetDashboradProjectCount();
  const vendorResult = useGetClientDashboard(employeeId, true);
  const summary = counts.dashboardCountDataMobileList?.dashboardCountDataMobile || {};
  const chart = charts.dashboardChartCountData?.dashboardChartCount || {};
  const assigned = chart.taskDataCountAssigned || {};
  const projects = projectResult.projectData?.data?.projectSummaries || [];
  const vendor = vendorResult.clientDashboardData?.data || {};
  const internal = !tairraUser?.isExternalEmployee;
  const taskTotal = statuses.reduce((sum, [, key]) => sum + (Number(assigned[key]) || 0), 0);
  const showAll = t('viewAll');
  const greeting = t(
    new Date().getHours() < 12
      ? 'goodMorning'
      : new Date().getHours() < 18
        ? 'goodAfternoon'
        : 'goodEvening'
  );
  const metrics = [
    ...(internal
      ? [
          [
            t('teamMembers'),
            summary.teamMemberCount,
            t('myTeam'),
            summary.loggedUserTeamMemberCount,
            paths.dashboard.user.list,
          ],
          [
            t('projects'),
            summary.totalAccessibleProjectCount,
            t('myProjects'),
            summary.projectOwnedByLoggedEmpCount,
            paths.dashboard.project.root,
          ],
        ]
      : []),
    [
      t('documents'),
      internal ? summary.totalDocumentCount : summary.totalDocumentByLoggedEmpCount,
      t('myDocuments'),
      summary.totalDocumentByLoggedEmpCount,
      paths.dashboard.document.root,
    ],
  ];
  return (
    <div className={styles.dashboard}>
      <div className={styles.pageHeading}>
        <div>
          <h1>{t('dashboard', { defaultValue: 'Dashboard' })}</h1>
          <p>
            {new Date().toLocaleDateString(i18n.language, {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </p>
        </div>
        <WorkspaceShortcuts />
      </div>
      <div className={styles.grid}>
        <section className={styles.welcome}>
          <div className={styles.welcomeHeading}>
            <span className={styles.avatar}>{tairraUser?.firstName?.charAt(0) || 'U'}</span>
            <div>
              <h2>
                {greeting}, {tairraUser?.firstName || t('user')}
              </h2>
              <p>
                {summary.teamNotice ||
                  t('welcomeSubtitle', { defaultValue: 'Everything you need, in one place.' })}
              </p>
            </div>
            <Button variant="outline" asChild>
              <Link href={paths.dashboard.task.root}>
                {t('tasks')} <span aria-hidden="true">{'\u2192'}</span>
              </Link>
            </Button>
          </div>
          <div className={styles.metrics}>
            <Link href={paths.dashboard.task.root}>
              <strong>{taskTotal}</strong>
              <span>{t('assignedTasks')}</span>
            </Link>
            {metrics.map(([label, value, subLabel, subValue, href]) => (
              <Link href={href} key={href}>
                <strong>{value || 0}</strong>
                <span>{label}</span>
                <small>
                  {subLabel}: {subValue || 0}
                </small>
              </Link>
            ))}
          </div>
        </section>
        {internal && (
          <Panel title={t('projectTasks')} href={paths.dashboard.project.root} action={showAll}>
            {projectResult.isDashboardProjectLoading ? (
              <p className={styles.empty} role="status">
                {t('loading', { defaultValue: 'Loading...' })}
              </p>
            ) : projectResult.dashboardProjectError ? (
              <p role="alert">{t('loadError', { defaultValue: 'Unable to load this section.' })}</p>
            ) : projects.length ? (
              <div className={styles.projectList}>
                {projects.map((project, index) => (
                  <div key={project.projectId || index}>
                    <span>{project.projectName}</span>
                    <progress
                      value={project.totalTasksCompleted || 0}
                      max={project.totalTasksAssigned || 1}
                    />
                    <small>
                      {t('completedTasks')}: {project.totalTasksCompleted || 0}{' '}
                      {t('remainingTasksToComplete')}:{' '}
                      {(project.totalTasksAssigned || 0) - (project.totalTasksCompleted || 0)}
                    </small>
                  </div>
                ))}
              </div>
            ) : (
              <p className={styles.empty}>{t('noTaskDetailsAvailableForProjects')}</p>
            )}
          </Panel>
        )}
        <Panel
          title={t('assignedTasks')}
          href={paths.dashboard.task.root}
          action={showAll}
          className={styles.mainPanel}
        >
          {charts.isLoading ? (
            <p role="status">{t('loading', { defaultValue: 'Loading...' })}</p>
          ) : charts.error ? (
            <p role="alert">{t('loadError', { defaultValue: 'Unable to load this section.' })}</p>
          ) : (
            <div className={styles.taskSummary}>
              <div className={styles.statusTiles}>
                {statuses.map(([label, key, color]) => (
                  <div key={key} style={{ borderInlineStartColor: color }}>
                    <span style={{ background: `${color}20` }}>{t(label)}</span>
                    <strong>{assigned[key] || 0}</strong>
                  </div>
                ))}
              </div>
              <Bars data={assigned} t={t} />
            </div>
          )}
        </Panel>
        {internal && (
          <Panel title={t('vendorTasks')} href={paths.dashboard.vendor.root} action={showAll}>
            <Bars
              data={{
                notStartedCount: vendor.totalNotStartedTasks,
                inProgressCount: vendor.totalInProgressTasks,
                onHoldCount: vendor.totalOnHoldTasks,
                requestedCount: vendor.totalSubmittedTasks,
                completedCount: vendor.totalCompletedTasks,
              }}
              t={t}
            />
          </Panel>
        )}
        <Panel
          title={t('listOfTasksAssignedToYou')}
          href={paths.dashboard.task.root}
          action={showAll}
          className={styles.mainPanel}
        >
          {charts.isLoading ? (
            <p role="status">{t('loading', { defaultValue: 'Loading...' })}</p>
          ) : (
            <div className={styles.employeeCards}>
              {(chart.assignedTasksByEmployee || []).slice(0, 5).map((row, index) => (
                <div key={row.employee?.id || index}>
                  <h3>{row.employee?.firstName}</h3>
                  <p>
                    {row.employee?.department?.name} {row.employee?.designation?.name}
                  </p>
                  <div className={styles.employeeStatuses}>
                    {statuses.map(([label, key, color]) => (
                      <span key={key} style={{ borderColor: color }}>
                        {t(label)}: {row.taskCount?.[key] || 0}
                      </span>
                    ))}
                  </div>
                  <strong>
                    {t('total')}: {row.taskCount?.allTaskCount || 0}
                  </strong>
                </div>
              ))}
              {!chart.assignedTasksByEmployee?.length && (
                <p className={styles.empty}>{t('noAssignedTaskDetailsAvailable')}</p>
              )}
            </div>
          )}
        </Panel>
      </div>
      {counts.error && (
        <p role="alert">{t('loadError', { defaultValue: 'Unable to load this section.' })}</p>
      )}
    </div>
  );
}
